import React, { useMemo, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TableContainer,
  Radio,
  Tooltip,
  Switch,
  FormControlLabel,
  InputLabel,
  Pagination,
  Divider,

} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import {
  useGetFlowsQuery,
  useRunWorkflowSmsMutation,
} from "../store/services/flowApi";
import dayjs from "dayjs";
import {
  useGetFilterReportsQuery,
  useGetSearchFilesQuery,
} from "../store/services/principleApi";
import { useGetCustomerServiceQuery } from "../store/services/resellerApi";
import {
  useAddCategoryMutation,
  useGetCategoriesQuery,
} from "../store/services/categoryApi";
// The WorkFlow component manages the workflow list, creation, and execution.
const WorkFlow = () => {
  const navigate = useNavigate();
  const { data: serviceResp, isLoading: isServiceLoading } =
    useGetCustomerServiceQuery();

  const hasServices = !isServiceLoading && (serviceResp?.data?.length ?? 0) > 0;
  const { data: categoryResp, isLoading: categoryLoading } =
    useGetCategoriesQuery();

  // Pagination state
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");

  const { data: flowsResp, isLoading: isFlowsLoading } = useGetFlowsQuery({
    page,
    search,
  });

  const [runWorkflowSms, { isLoading: isRunLoading }] =
    useRunWorkflowSmsMutation();
  const [addCategory] = useAddCategoryMutation();

  const workflows = useMemo(() => {
    const rows = flowsResp?.data || [];
    return rows.map((row) => ({
      id: row.cf_id,
      name: row.cf_name,
      createdOn: row.create_date,
      lastRunOn: row.last_run_on || null,
      createdBy: "Fabmediainf",
      status: row.status || "Executed",
      category: row.category,
      flowData: row.flow_data,
      customerId: row.customer_id,
    }));
  }, [flowsResp]);

  // UI filter states (visual placeholders)
  const [filterBy, setFilterBy] = useState("Workflow ID");
  const [sortBy, setSortBy] = useState("Workflow Name");
  const [createdFilter, setCreatedFilter] = useState("Created On");

  const [createOpen, setCreateOpen] = useState(false);
  const [workflowName, setWorkflowName] = useState("");
  const categories = useMemo(() => {
    return categoryResp?.data?.map((c) => c.category_name) || [];
  }, [categoryResp]);
  const [categorySearch, setCategorySearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const openCreate = () => {
    setWorkflowName("");
    setSelectedCategory("");
    setCategorySearch("");
    setCreateOpen(true);
  };

  const closeCreate = () => setCreateOpen(false);

  const handleCreateWorkflow = () => {
    if (!workflowName.trim() || !selectedCategory) {
      alert("Please enter Workflow Name and select Category.");
      return;
    }

    const newWorkflow = {
      id: Date.now(),
      name: workflowName.trim(),
      createdOn: new Date().toISOString(),
      lastRunOn: "",
      createdBy: "Fabmediainf",
      status: "Draft",
      category: selectedCategory,
    };
    localStorage.setItem(
      `workflow-meta-${newWorkflow.id}`,
      JSON.stringify({
        name: newWorkflow.name,
        category: selectedCategory,
      }),
    );
    navigate(
      `/workflow/${newWorkflow.id}?name=${encodeURIComponent(
        newWorkflow.name,
      )}&category=${encodeURIComponent(selectedCategory)}`,
      {
        state: { workflow: newWorkflow },
      },
    );
  };

  const visibleCategories = useMemo(
    () =>
      categories.filter((c) =>
        c.toLowerCase().includes(categorySearch.toLowerCase()),
      ),
    [categories, categorySearch],
  );

  const handleCreateNewCategory = async () => {
    const name = window.prompt("Enter new category name:");
    if (!name) return;

    const trimmed = name.trim();
    if (!trimmed) return;

    try {
      await addCategory({
        category_name: trimmed,
      }).unwrap();

      setSelectedCategory(trimmed);
      setCategorySearch("");
    } catch (err) {
      alert(err?.data?.message || "Error adding category");
    }
  };

  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  const [fileSource, setFileSource] = useState("local");
  const [selectedLocalFile, setSelectedLocalFile] = useState(null);
  const [selectedDbFile, setSelectedDbFile] = useState(null);
  const [runFileName, setRunFileName] = useState("");

  const [enableFilterTill, setEnableFilterTill] = useState(false);
  const [filterDatatill_days, setFilterDatatill_days] = useState(0);

  const [enableSplit, setEnableSplit] = useState(false);
  const [batch_size, setbatch_size] = useState("");
  const [time_interval, settime_interval] = useState("");

  const [enableSchedule, setEnableSchedule] = useState(false);
  const [dateTime, setDateTime] = useState(
    dayjs(new Date()).format("YYYY-MM-DDTHH:mm"),
  );
  const [sendTime, setSendTime] = useState(
    dayjs(new Date()).format("YYYY-MM-DDTHH:mm"),
  );
  const [minDateTime, setMinDateTime] = useState(
    dayjs().format("YYYY-MM-DDTHH:mm"),
  );

  const [enableTtlExpire, setEnableTtlExpire] = useState(false);
  const [ttlExpireHours, setTtlExpireHours] = useState(8);

  const fileInputRef = useRef(null);

  const { data: dbFiles, isLoading: dbFilesLoading } = useGetSearchFilesQuery(
    undefined,
    { skip: !runDialogOpen },
  );

  const { data: filterReports, isLoading: filterLoading } =
    useGetFilterReportsQuery(undefined, { skip: !runDialogOpen });

  const mergedDbFiles = useMemo(() => {
    const searchFiles =
      dbFiles?.data?.map((f) => ({
        file_path: f.path,
        filter_name: f.filter_name || "Search File",
        totalcount: f.totalcount,
        page_no: f.page_no,
        fieldsinfile: f.fieldsinfile,
        createdat: f.createdat,
      })) || [];

    const reportFiles =
      filterReports?.data?.map((r) => ({
        file_path: r.file_path,
        filter_name: r.filter_name || "Saved Filter",
        totalcount: r.records_count,
        page_no: "-",
        fieldsinfile: "Mobile",
        createdat: r.created_at,
      })) || [];

    return [...searchFiles, ...reportFiles];
  }, [dbFiles, filterReports]);

  const handlePlayClick = (wf) => {
    setSelectedWorkflow(wf);
    setRunDialogOpen(true);
    setFileSource("local");
    setSelectedLocalFile(null);
    setSelectedDbFile(null);
    setRunFileName("");
    setEnableFilterTill(false);
    setFilterDatatill_days(0);
    setEnableSplit(false);
    setbatch_size("");
    settime_interval("");
    setEnableSchedule(false);
    setSendTime(dayjs(new Date()).format("YYYY-MM-DDTHH:mm"));
    setEnableTtlExpire(false);
    setTtlExpireHours(8);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleLocalFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedLocalFile(file);
      setSelectedDbFile(null);
      setRunFileName(file.name);
    }
  };

  const handleDbFileSelect = (file) => {
    setSelectedDbFile(file);
    setSelectedLocalFile(null);
    setRunFileName(`From DB: ${file.filter_name}`);
  };

  const handleRunSubmit = async () => {
    if (!selectedWorkflow) return;
    if (!selectedLocalFile && !selectedDbFile) {
      alert("Please select a file");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("flow_id", selectedWorkflow.id);
      formData.append("flow_name", selectedWorkflow.name);

      const updatedFlowData = (selectedWorkflow.flowData || []).map((node) => ({
        ...node,
        filtertill: enableFilterTill,
        filterDatatill_days: filterDatatill_days, // FIXED: Use state directly instead of ?? operator
        bachsize: batch_size,
        time_interval: time_interval,
        shedule: enableSchedule,
        time_to_send: sendTime.replace("T", " "),
        ttl_expired: enableTtlExpire ? ttlExpireHours : null,
      }));

      formData.append("flowData", JSON.stringify(updatedFlowData));
      formData.append("filtertill", enableFilterTill);
      formData.append("filterDatatill_days", filterDatatill_days);
      formData.append("bachsize", batch_size);
      formData.append("time_interval", time_interval);
      formData.append("shedule", enableSchedule);
      formData.append("time_to_send", sendTime.replace("T", " "));
      formData.append("ttl_expired", enableTtlExpire ? ttlExpireHours : null);
      formData.append("fileSource", fileSource);

      if (fileSource === "local" && selectedLocalFile) {
        formData.append("upload_file", selectedLocalFile);
      }

      if (fileSource === "db" && selectedDbFile) {
        formData.append("dbFile", JSON.stringify(selectedDbFile));
      }

      const resp = await runWorkflowSms(formData).unwrap();
      alert(resp?.message || "Workflow SMS campaign created successfully!");
      setRunDialogOpen(false);
      setSelectedLocalFile(null);
      setSelectedDbFile(null);
      setRunFileName("");
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.error ||
        err?.message ||
        "Failed to run workflow.";
      alert(msg);
    }
  };

  if (!isServiceLoading && !hasServices) {
    return (
      <Box sx={{ textAlign: "center", mt: 10, fontSize: "1rem" }}>
        <Typography variant="h6" color="error">
          You have not been assigned any service. Please contact your
          administrator.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>
        Workflow
      </Typography>

      {/* TOP FILTER / SEARCH BAR */}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", elevation: 2, borderRadius: "0.5rem", padding: "1rem", backgroundColor: "#ffffff52", backdropFilter: "blur(10px)", boxShadow: "0 4px 12px rgba(14,165,233,0.1)", mb: 2 }}>
        {/* Left: Filter By dropdowns */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 600,
              color: "text.secondary",
            }}
          >
            Filter By
          </Typography>

          <FormControl size="small">
            <Select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              sx={{
                fontSize: "0.8rem",
                minWidth: 120,
                "& .MuiSelect-select": { py: 0.6 },
              }}
            >
              <MenuItem value="Workflow ID" sx={{ fontSize: "0.8rem" }}>
                Workflow ID
              </MenuItem>
              <MenuItem value="Workflow Name" sx={{ fontSize: "0.8rem" }}>
                Workflow Name
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small">
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{
                fontSize: "0.8rem",
                minWidth: 140,
                "& .MuiSelect-select": { py: 0.6 },
              }}
            >
              <MenuItem value="Workflow Name" sx={{ fontSize: "0.8rem" }}>
                Workflow Name
              </MenuItem>
              <MenuItem value="Created On" sx={{ fontSize: "0.8rem" }}>
                Created On
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small">
            <Select
              value={createdFilter}
              onChange={(e) => setCreatedFilter(e.target.value)}
              sx={{
                fontSize: "0.8rem",
                minWidth: 120,
                "& .MuiSelect-select": { py: 0.6 },
              }}
            >
              <MenuItem value="Created On" sx={{ fontSize: "0.8rem" }}>
                Created On
              </MenuItem>
              <MenuItem value="Last Run On" sx={{ fontSize: "0.8rem" }}>
                Last Run On
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* Right: Search input + Create button */}
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Workflow Name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              minWidth: 220,
              "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.8 },
            }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setPage(1);
              setSearch(searchText);
            }}
            sx={{ height: 32, fontSize: "0.8rem", textTransform: "none" }}
          >
            Search
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            onClick={openCreate}
            sx={{
              height: 32,
              textTransform: "none",
              fontSize: "0.8rem",
            }}
          >
            Create Workflow
          </Button>
        </Stack>
      </Box>


      {/* WORKFLOW LIST TABLE */}
      <Table size="small" sx={{ borderRadius: 0.5, overflow: "hidden" }}>
        <TableHead
          sx={{
            backgroundColor: "#068eddff",
            color: "white",
          }}
        >
          <TableRow>
            <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600, color: "white" }}>
              Workflow ID
            </TableCell>
            <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600, color: "white" }}>
              Workflow Name
            </TableCell>
            <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600, color: "white" }}>
              Created On
            </TableCell>
            <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600, color: "white" }}>
              Category Name
            </TableCell>
            <TableCell sx={{ fontSize: "0.85rem", fontWeight: 600, color: "white" }}>
              Status
            </TableCell>
            <TableCell
              sx={{ fontSize: "0.85rem", fontWeight: 600, color: "white" }}
              align="center"
            >
              Action
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody sx={{ background: "rgba(255, 255, 255, 0.41)" }}>
          {isFlowsLoading && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                  Loading workflows...
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {!isFlowsLoading &&
            workflows.map((wf) => (
              <TableRow
                key={wf.id}
                hover
                sx={{
                  "& td": { fontSize: "0.82rem", py: 1.2 },
                }}
              >
                <TableCell>{wf.id}</TableCell>
                <TableCell>{wf.name}</TableCell>
                <TableCell>
                  {wf.createdOn
                    ? new Date(wf.createdOn).toLocaleString()
                    : "-"}
                </TableCell>
                <TableCell>{wf.category}</TableCell>
                <TableCell>{wf.status}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Run Workflow" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handlePlayClick(wf)}
                    >
                      <PlayCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Workflow" arrow>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/workflow/edit/${wf.id}`)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

          {!isFlowsLoading && workflows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                  No workflows found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={flowsResp?.pagination?.totalPages || 1}
          page={page}
          onChange={(e, value) => setPage(value)}
          size="small"
        />
      </Box>
      <Box display="flex" justifyContent="space-between" mt={1} px={1}>
        <Typography sx={{ fontSize: "0.8rem" }}>
          Total Records: {flowsResp?.pagination?.totalRecords || 0}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem" }}>
          Page {page} of {flowsResp?.pagination?.totalPages || 1}
        </Typography>
      </Box>

      {/* CREATE WORKFLOW DIALOG (FIXED) */}
      <Dialog open={createOpen} onClose={closeCreate} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Workflow Name
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Box mb={2}>
            <TextField
              fullWidth
              size="small"
              label="Workflow Name"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              sx={{
                "& .MuiInputBase-input": { fontSize: "0.85rem" },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
            <Typography
              sx={{
                mt: 0.5,
                fontSize: "0.75rem",
                color: "text.secondary",
                fontStyle: "italic",
              }}
            >
              Note: The workflow name cannot be changed once saved. Make sure
              it&apos;s final before proceeding.
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, mb: 0.5 }}>
            Category
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              renderValue={(value) =>
                value ? value : <span style={{ color: "#9e9e9e" }}>Select</span>
              }
              sx={{ "& .MuiSelect-select": { fontSize: "0.85rem" } }}
              MenuProps={{
                PaperProps: {
                  sx: { maxHeight: 300 },
                },
              }}
            >
              {/* Search input (non-selectable) */}
              <MenuItem
                disabled
                sx={{
                  "&.Mui-disabled": {
                    opacity: 1,
                    backgroundColor: "transparent",
                  },
                  p: 1,
                }}
              >
                <TextField
                  autoFocus
                  size="small"
                  placeholder="Search categories..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.5 },
                  }}
                />
              </MenuItem>

              <Divider sx={{ my: 0.5 }} />

              {/* Filtered category list */}
              {visibleCategories.length > 0 ? (
                visibleCategories.map((cat) => (
                  <MenuItem key={cat} value={cat} sx={{ fontSize: "0.85rem" }}>
                    {cat}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled sx={{ fontSize: "0.85rem", opacity: 0.7 }}>
                  No categories found
                </MenuItem>
              )}

              <Divider sx={{ my: 0.5 }} />

              {/* Create new category option */}
              <MenuItem
                onClick={handleCreateNewCategory}
                sx={{
                  fontSize: "0.85rem",
                  color: "#000",
                  fontWeight: 500,

                }}
              >
                <AddIcon fontSize="small" sx={{ mr: 1 }} />
                Create New Category
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={handleCreateWorkflow}
            sx={{ textTransform: "none", fontSize: "0.8rem" }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={closeCreate}
            sx={{ textTransform: "none", fontSize: "0.8rem" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* RUN WORKFLOW DIALOG */}
      <Dialog
        open={runDialogOpen}
        onClose={() => setRunDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
          Run Workflow
          {selectedWorkflow ? `: ${selectedWorkflow.name}` : ""}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant={fileSource === "local" ? "contained" : "outlined"}
              onClick={() => setFileSource("local")}
              sx={{ textTransform: "none", fontSize: "0.8rem" }}
            >
              Choose From Local System
            </Button>
            <Button
              size="small"
              variant={fileSource === "db" ? "contained" : "outlined"}
              onClick={() => setFileSource("db")}
              sx={{ textTransform: "none", fontSize: "0.8rem" }}
            >
              Choose From Database
            </Button>
          </Box>

          {fileSource === "local" && (
            <Box>
              <Button
                variant="outlined"
                size="small"
                component="label"
                sx={{ textTransform: "none", fontSize: "0.8rem" }}
              >
                Select File
                <input
                  type="file"
                  hidden
                  ref={fileInputRef}
                  onChange={handleLocalFileChange}
                />
              </Button>
              <Typography sx={{ mt: 1, fontSize: "0.8rem" }}>
                Selected:{" "}
                {runFileName ? (
                  <strong>{runFileName}</strong>
                ) : (
                  <span style={{ color: "#888" }}>No file selected</span>
                )}
              </Typography>
            </Box>
          )}

          {fileSource === "db" && (
            <Box sx={{ mt: 1 }}>
              {dbFilesLoading || filterLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : mergedDbFiles.length > 0 ? (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead
                      sx={{
                        bgcolor: "#0288d1",
                        "& th": {
                          color: "white",
                          fontSize: "0.75rem",
                          borderBottom: "0.5px solid #B0B0B0",
                        },
                      }}
                    >
                      <TableRow>
                        <TableCell
                          sx={{
                            width: "10px",
                            borderRight: "0.5px solid #B0B0B0",
                          }}
                        >
                          Sr.No
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          Filter Name
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            borderRight: "0.5px solid #B0B0B0",
                          }}
                        >
                          File No
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            borderRight: "0.5px solid #B0B0B0",
                          }}
                        >
                          Total Count
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            borderRight: "0.5px solid #B0B0B0",
                          }}
                        >
                          Fields In File
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            borderRight: "0.5px solid #B0B0B0",
                          }}
                        >
                          Created At
                        </TableCell>
                        <TableCell
                          sx={{
                            width: "10px",
                            borderRight: "0.5px solid #B0B0B0",
                          }}
                        >
                          Select
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mergedDbFiles.map((file, index) => (
                        <TableRow key={`${file.file_path}-${index}`} hover>
                          <TableCell
                            sx={{
                              padding: "8px",
                              borderRight: "0.5px solid #B0B0B0",
                            }}
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "8px",
                              textAlign: "center",
                              borderRight: "0.5px solid #B0B0B0",
                            }}
                          >
                            {file.filter_name}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "8px",
                              textAlign: "center",
                              borderRight: "0.5px solid #B0B0B0",
                            }}
                          >
                            {file.page_no}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "8px",
                              textAlign: "center",
                              borderRight: "0.5px solid #B0B0B0",
                            }}
                          >
                            {file.totalcount}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "8px",
                              textAlign: "center",
                              borderRight: "0.5px solid #B0B0B0",
                            }}
                          >
                            {["", null, undefined, "NA"].includes(
                              file.fieldsinfile,
                            )
                              ? "Mobile"
                              : file.fieldsinfile}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "8px",
                              textAlign: "center",
                              borderRight: "0.5px solid #B0B0B0",
                            }}
                          >
                            {file.createdat
                              ? dayjs(file.createdat).format("DD/MM/YYYY")
                              : "-"}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "8px",
                              borderRight: "0.5px solid #B0B0B0",
                            }}
                          >
                            <Radio
                              size="small"
                              checked={
                                selectedDbFile?.file_path === file.file_path
                              }
                              onChange={() => handleDbFileSelect(file)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ fontSize: "0.8rem" }}>
                  No files available in database
                </Typography>
              )}
              <Typography sx={{ mt: 1, fontSize: "0.8rem" }}>
                Selected:{" "}
                {runFileName ? (
                  <strong>{runFileName}</strong>
                ) : (
                  <span style={{ color: "#888" }}>No file selected</span>
                )}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 1.5 }}>
              Additional Configurations
            </Typography>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={enableFilterTill}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEnableFilterTill(checked);
                      if (!checked) setFilterDatatill_days(0);
                    }}
                  />
                }
                label="Filter From LC"
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
              />
              {enableFilterTill && (
                <FormControl size="small" sx={{ width: 150, ml: 2 }}>
                  <InputLabel sx={{ fontSize: "0.75rem", top: "-4px" }}>
                    Filter Till
                  </InputLabel>
                  <Select
                    value={filterDatatill_days}
                    label="Filter Till"
                    onChange={(e) => setFilterDatatill_days(e.target.value)}
                    sx={{
                      fontSize: "0.8rem",
                      height: 32,
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                  >
                    <MenuItem sx={{ fontSize: "0.8rem" }} value={1}>
                      Today
                    </MenuItem>
                    <MenuItem sx={{ fontSize: "0.8rem" }} value={2}>
                      Yesterday
                    </MenuItem>
                    {Array.from({ length: 28 }, (_, i) => {
                      const day = i + 3;
                      return (
                        <MenuItem
                          key={day}
                          value={day}
                          sx={{ fontSize: "0.8rem" }}
                        >
                          Last {day} days
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={enableSplit}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEnableSplit(checked);
                      if (!checked) {
                        setbatch_size("");
                        settime_interval("");
                      }
                    }}
                  />
                }
                label="Enable Split"
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
              />
              {enableSplit && (
                <Box sx={{ display: "flex", gap: 2, ml: 2, mt: 1 }}>
                  <TextField
                    label="Interval (Minutes)"
                    type="number"
                    size="small"
                    value={time_interval}
                    onChange={(e) => settime_interval(e.target.value)}
                    error={time_interval && time_interval < 10}
                    helperText={
                      time_interval && time_interval < 10
                        ? "Minimum 10 minutes required"
                        : ""
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 10 }}
                    sx={{
                      width: 180,
                      "& .MuiInputBase-root": { fontSize: "0.8rem" },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                    }}
                  />
                  <TextField
                    label="Batch Size"
                    type="number"
                    size="small"
                    value={batch_size}
                    onChange={(e) => setbatch_size(e.target.value)}
                    error={batch_size && batch_size < 1000}
                    helperText={
                      batch_size && batch_size < 1000
                        ? "Minimum batch size is 1000"
                        : ""
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 1000 }}
                    sx={{
                      width: 180,
                      "& .MuiInputBase-root": { fontSize: "0.8rem" },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={enableSchedule}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setEnableSchedule(isChecked);
                      if (!isChecked) {
                        const now = dayjs().format("YYYY-MM-DDTHH:mm");
                        setDateTime(now);
                        setSendTime(now);
                      } else {
                        setMinDateTime(dayjs().format("YYYY-MM-DDTHH:mm"));
                      }
                    }}
                  />
                }
                label="Schedule For Later"
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
              />
              {enableSchedule && (
                <Box sx={{ ml: 2, mt: 1 }}>
                  <TextField
                    label="Schedule Date"
                    type="datetime-local"
                    size="small"
                    value={sendTime}
                    onChange={(e) => {
                      const selectedDateTime = e.target.value;
                      const now = dayjs().format("YYYY-MM-DDTHH:mm");
                      if (dayjs(selectedDateTime).isBefore(now)) {
                        alert("Schedule time cannot be in the past");
                        setSendTime(now);
                      } else {
                        setSendTime(selectedDateTime);
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minDateTime }}
                    sx={{
                      width: 250,
                      "& .MuiInputBase-root": { fontSize: "0.8rem" },
                      "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={enableTtlExpire}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEnableTtlExpire(checked);
                      if (!checked) setTtlExpireHours(8);
                    }}
                  />
                }
                label="TTL Expire Time"
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
              />
              {enableTtlExpire && (
                <FormControl size="small" sx={{ width: 150, ml: 2 }}>
                  <InputLabel sx={{ fontSize: "0.75rem", top: "-4px" }}>
                    Hours
                  </InputLabel>
                  <Select
                    value={ttlExpireHours}
                    label="Hours"
                    onChange={(e) => setTtlExpireHours(e.target.value)}
                    sx={{
                      fontSize: "0.8rem",
                      height: 32,
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                  >
                    {Array.from({ length: 17 }, (_, i) => {
                      const hour = i + 8; // 8 to 24
                      return (
                        <MenuItem
                          key={hour}
                          value={hour}
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {hour} hours
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleRunSubmit}
            disabled={isRunLoading}
            sx={{ textTransform: "none", fontSize: "0.8rem" }}
          >
            {isRunLoading ? "Running..." : "Run Workflow"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setRunDialogOpen(false)}
            sx={{ textTransform: "none", fontSize: "0.8rem" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkFlow;